/**
 * PMPublishStateIndicator - Visual State Badge
 *
 * Shows current publish state: DRAFT → SYNCED → VALIDATED → PUBLISHED
 * Color-coded with clear visual hierarchy.
 */

// State configuration
const STATE_CONFIG = {
  DRAFT: {
    label: "Draft",
    description: "Excel generated, not yet synced",
    color: "bg-gray-100 text-gray-600 border-gray-300",
    dotColor: "bg-gray-400",
    icon: "📝",
  },
  SYNCED: {
    label: "Synced",
    description: "Excel parsed, validation pending",
    color: "bg-blue-100 text-blue-700 border-blue-300",
    dotColor: "bg-blue-500",
    icon: "🔄",
  },
  VALIDATED: {
    label: "Validated",
    description: "Ready to publish",
    color: "bg-green-100 text-green-700 border-green-300",
    dotColor: "bg-green-500",
    icon: "✓",
  },
  PUBLISHED: {
    label: "Published",
    description: "In production",
    color: "bg-purple-100 text-purple-700 border-purple-300",
    dotColor: "bg-purple-500",
    icon: "🚀",
  },
};

// State order for progress visualization
const STATE_ORDER = ["DRAFT", "SYNCED", "VALIDATED", "PUBLISHED"];

export default function PMPublishStateIndicator({
  workOrder,
  onClick,
  showProgress = false,
  compact = false,
}) {
  const state = workOrder?.publish_state || "DRAFT";
  const config = STATE_CONFIG[state] || STATE_CONFIG.DRAFT;
  const stateIndex = STATE_ORDER.indexOf(state);

  // Compact badge only
  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
        title={config.description}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`}></span>
        {config.label}
      </span>
    );
  }

  // Full indicator with optional click handler
  const Component = onClick ? "button" : "div";

  return (
    <Component
      onClick={onClick}
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border
        ${config.color}
        ${onClick ? "cursor-pointer hover:brightness-95 transition-all" : ""}
      `}
      title={config.description}
    >
      <span>{config.icon}</span>
      <span className="font-medium">{config.label}</span>

      {/* Progress dots */}
      {showProgress && (
        <div className="flex items-center gap-1 ml-2">
          {STATE_ORDER.map((s, idx) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx <= stateIndex
                  ? STATE_CONFIG[s].dotColor
                  : "bg-gray-200"
              }`}
              title={STATE_CONFIG[s].label}
            />
          ))}
        </div>
      )}
    </Component>
  );
}

// Mini version for inline use
export function PMPublishStateBadge({ state }) {
  const config = STATE_CONFIG[state] || STATE_CONFIG.DRAFT;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${config.color}`}
    >
      <span className={`w-1 h-1 rounded-full ${config.dotColor}`}></span>
      {config.label}
    </span>
  );
}

// Progress stepper visualization
export function PMPublishStateProgress({ currentState }) {
  const stateIndex = STATE_ORDER.indexOf(currentState || "DRAFT");

  return (
    <div className="flex items-center gap-2">
      {STATE_ORDER.map((state, idx) => {
        const config = STATE_CONFIG[state];
        const isActive = idx === stateIndex;
        const isComplete = idx < stateIndex;

        return (
          <div key={state} className="flex items-center">
            {/* Step indicator */}
            <div
              className={`
                flex items-center justify-center w-8 h-8 rounded-full border-2
                ${
                  isActive
                    ? `${config.color} border-current`
                    : isComplete
                      ? "bg-green-100 border-green-500 text-green-700"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                }
              `}
              title={config.description}
            >
              {isComplete ? "✓" : idx + 1}
            </div>

            {/* Label */}
            <span
              className={`ml-2 text-sm font-medium ${
                isActive
                  ? "text-gray-900"
                  : isComplete
                    ? "text-green-600"
                    : "text-gray-400"
              }`}
            >
              {config.label}
            </span>

            {/* Connector line */}
            {idx < STATE_ORDER.length - 1 && (
              <div
                className={`w-8 h-0.5 mx-2 ${
                  idx < stateIndex ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
